'use client';

import Image from 'next/image';
import { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Check,
  Package,
  Camera,
  Radio,
  Thermometer,
  Droplet,
  Power,
  Tag,
  Shield,
  Download,
  Wifi,
  MapPin,
  Zap,
  HardDrive,
  Eye,
  RadioIcon,
  Gauge,
  Users,
  Building2,
  ShoppingCart,
  HeadphonesIcon,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

type DeviceCategory = 'All' | 'Vehicle Tracking' | 'Cameras' | 'Livestock' | 'Sensors' | 'Asset Tracking';

interface Device {
  id: string;
  name: string;
  category: DeviceCategory[];
  description: string;
  specs: string[];
  useCases: string[];
  requirements: string[];
  gradient: string;
  icon: React.ReactNode;
}

const devices: Device[] = [
  {
    id: 'gt-x1',
    name: 'GT-X1 GPS Tracker',
    category: ['Vehicle Tracking'],
    description: 'Hardwired OBD-II vehicle tracker with advanced telematics capabilities for comprehensive fleet management.',
    specs: ['4G LTE Cat-1', 'GPS/GLONASS/BeiDou', '3-axis accelerometer', '9-36V input', 'IP67', 'Tamper detection', 'Backup battery'],
    useCases: ['Fleet tracking', 'Stolen vehicle recovery', 'Driver behavior monitoring'],
    requirements: ['Professional installation', '12V/24V power'],
    gradient: 'from-neon-cyan/20 to-neon-blue/20',
    icon: <Radio className="h-6 w-6" />,
  },
  {
    id: 'gt-x2',
    name: 'GT-X2 Portable Tracker',
    category: ['Asset Tracking'],
    description: 'Magnetic asset tracker with ultra-long battery life for container and equipment monitoring.',
    specs: ['LTE-M/NB-IoT', '5-year battery', 'IP67', 'Magnetic mount', 'Compact design'],
    useCases: ['Container tracking', 'Trailer tracking', 'Equipment monitoring'],
    requirements: ['No installation needed', 'Magnetic surface'],
    gradient: 'from-neon-purple/20 to-neon-magenta/20',
    icon: <MapPin className="h-6 w-6" />,
  },
  {
    id: 'gt-cam-pro',
    name: 'GT-CAM Pro',
    category: ['Cameras'],
    description: 'Dual AI dashcam with advanced driver monitoring and incident recording capabilities.',
    specs: ['1080p front+interior', 'ADAS+DMS AI', 'Night vision', '4G upload', 'microSD up to 256GB', 'G-sensor', 'GPS'],
    useCases: ['Driver monitoring', 'Evidence recording', 'Insurance claims', 'Fleet safety'],
    requirements: ['12V power', 'Windshield mount'],
    gradient: 'from-neon-magenta/20 to-neon-purple/20',
    icon: <Camera className="h-6 w-6" />,
  },
  {
    id: 'gt-cam-360',
    name: 'GT-CAM 360',
    category: ['Cameras'],
    description: 'Quad camera system providing complete vehicle surround view with professional-grade recording.',
    specs: ['4x 720p cameras', 'DVR', 'HDD storage', '4G streaming', '360° coverage'],
    useCases: ['Full vehicle surround view', 'Incident recording', 'Security monitoring'],
    requirements: ['Professional installation', '24V power'],
    gradient: 'from-neon-blue/20 to-neon-teal/20',
    icon: <Eye className="h-6 w-6" />,
  },
  {
    id: 'gt-collar-v2',
    name: 'GT-Collar V2',
    category: ['Livestock'],
    description: 'Solar-powered GPS collar for livestock tracking with multi-connectivity options.',
    specs: ['Solar-powered + rechargeable', 'IP68', 'GPS + LoRa + 4G', 'Weight: 350g', '2-year operational life'],
    useCases: ['Cattle tracking', 'Game farm monitoring', 'Anti-theft protection'],
    requirements: ['LoRa gateway for farm coverage', '4G fallback'],
    gradient: 'from-neon-teal/20 to-neon-cyan/20',
    icon: <RadioIcon className="h-6 w-6" />,
  },
  {
    id: 'gt-temp',
    name: 'GT-Temp Sensor',
    category: ['Sensors'],
    description: 'Cold chain temperature monitor ensuring compliance for pharmaceutical and food logistics.',
    specs: ['Bluetooth 5.0', '-40°C to +80°C range', 'FDA compliant', 'Real-time alerts'],
    useCases: ['Pharmaceutical transport', 'Food logistics', 'Cold storage monitoring'],
    requirements: ['Gateway device in vehicle', 'Calibration every 12 months'],
    gradient: 'from-neon-cyan/20 to-neon-teal/20',
    icon: <Thermometer className="h-6 w-6" />,
  },
  {
    id: 'gt-obd-pro',
    name: 'GT-OBD Pro',
    category: ['Vehicle Tracking', 'Sensors'],
    description: 'OBD-II diagnostic device with comprehensive vehicle health monitoring and predictive maintenance.',
    specs: ['CAN Bus', 'J1939/J1708 support', 'Plug-and-play', 'Engine codes', 'Fuel consumption', 'Vehicle health'],
    useCases: ['Fleet diagnostics', 'Predictive maintenance', 'Fuel optimization'],
    requirements: ['OBD-II port', 'Compatible vehicles 1996+'],
    gradient: 'from-neon-amber/20 to-neon-magenta/20',
    icon: <Gauge className="h-6 w-6" />,
  },
  {
    id: 'gt-asset-tag',
    name: 'GT-Asset Tag',
    category: ['Asset Tracking'],
    description: 'Ultra-compact asset tracker with extended battery life for high-value equipment monitoring.',
    specs: ['LTE-M', '3-year battery', 'IP67', 'Compact form factor', 'Adhesive mount'],
    useCases: ['Tool tracking', 'Equipment monitoring', 'High-value assets'],
    requirements: ['No installation', 'Adhesive or screw mount'],
    gradient: 'from-neon-purple/20 to-neon-blue/20',
    icon: <Tag className="h-6 w-6" />,
  },
  {
    id: 'gt-fuel',
    name: 'GT-Fuel Sensor',
    category: ['Sensors'],
    description: 'Capacitive fuel level monitor with anti-siphon detection for fuel theft prevention.',
    specs: ['Analog/digital output', 'Anti-siphon detection', 'High accuracy', 'Real-time monitoring'],
    useCases: ['Fuel theft prevention', 'Consumption monitoring', 'Fleet cost optimization'],
    requirements: ['Tank installation by technician'],
    gradient: 'from-neon-blue/20 to-neon-cyan/20',
    icon: <Droplet className="h-6 w-6" />,
  },
  {
    id: 'gt-pto',
    name: 'GT-PTO Sensor',
    category: ['Sensors'],
    description: 'Power Take-Off monitor detecting auxiliary equipment usage for construction and municipal vehicles.',
    specs: ['PTO detection', 'Usage tracking', 'Integration ready'],
    useCases: ['Construction vehicles', 'Municipal services', 'Auxiliary equipment monitoring'],
    requirements: ['Integration with vehicle PTO system'],
    gradient: 'from-neon-magenta/20 to-neon-amber/20',
    icon: <Power className="h-6 w-6" />,
  },
];

const categories: DeviceCategory[] = ['All', 'Vehicle Tracking', 'Cameras', 'Livestock', 'Sensors', 'Asset Tracking'];

export default function GadgetsPage() {
  const [selectedCategory, setSelectedCategory] = useState<DeviceCategory>('All');

  const filteredDevices = selectedCategory === 'All'
    ? devices
    : devices.filter(device => device.category.includes(selectedCategory));

  return (
    <>
      {/* Hero Section */}
      <section className="gradient-hero relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-neon-cyan/10 blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-neon-purple/10 blur-[120px]" />
        
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full glass-card px-4 py-2"
            >
              <Package className="h-4 w-4 text-neon-cyan" />
              <span className="text-sm font-medium text-slate-300">Hardware Portfolio</span>
            </motion.div>
            
            <h1 className="mb-6 text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
              Enterprise-Grade Hardware
              <br />
              <span className="text-gradient">for Every Use Case</span>
            </h1>
            
            <p className="mx-auto mb-8 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              We supply, configure, and manage the full hardware stack. From GPS trackers to AI dashcams,
              every device is pre-provisioned, OTA-updated, and backed by our certified technician network.
            </p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.gadgets.src}
                  alt={PAGE_HERO_IMAGES.gadgets.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="#devices">
                <button className="btn-neon rounded-full px-8 py-3.5 text-base font-semibold flex items-center gap-2">
                  Explore Devices
                  <ArrowRight className="h-5 w-5" />
                </button>
              </Link>
              <Link href="/contact">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-base font-semibold">
                  Request Quote
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section id="devices" className="gradient-section border-y border-white/10 py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full px-6 py-2.5 text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'glass-card-heavy text-neon-cyan border border-neon-cyan/30 glow-cyan'
                    : 'glass-card text-slate-300 hover:text-white hover:border-white/20'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Device Grid */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filteredDevices.map((device, index) => (
                <motion.div
                  key={device.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className="group glass-card rounded-2xl p-6 transition-all hover:glass-card-heavy"
                >
                  {/* Device Image Placeholder */}
                  <div className={`mb-4 flex h-48 items-center justify-center rounded-xl bg-gradient-to-br ${device.gradient} relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]" />
                    <div className="relative z-10 text-white/80">
                      {device.icon}
                    </div>
                  </div>

                  {/* Category Tags */}
                  <div className="mb-3 flex flex-wrap gap-2">
                    {device.category.map((cat) => (
                      <span
                        key={cat}
                        className="rounded-full bg-neon-cyan/10 px-3 py-1 text-xs font-medium text-neon-cyan"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>

                  {/* Device Name */}
                  <h3 className="mb-2 text-xl font-bold text-white">{device.name}</h3>

                  {/* Description */}
                  <p className="mb-4 text-sm leading-relaxed text-slate-300">{device.description}</p>

                  {/* Key Specs */}
                  <div className="mb-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-300">Key Specs</p>
                    <div className="flex flex-wrap gap-2">
                      {device.specs.slice(0, 4).map((spec) => (
                        <span
                          key={spec}
                          className="rounded-lg bg-white/8 px-2.5 py-1 text-xs text-slate-300"
                        >
                          {spec}
                        </span>
                      ))}
                      {device.specs.length > 4 && (
                        <span className="rounded-lg bg-white/8 px-2.5 py-1 text-xs text-slate-300">
                          +{device.specs.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Use Cases */}
                  <div className="mb-4 border-t border-white/10 pt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-300">Use Cases</p>
                    <ul className="space-y-1">
                      {device.useCases.map((useCase) => (
                        <li key={useCase} className="flex items-center gap-2 text-xs text-slate-300">
                          <Check className="h-3.5 w-3.5 text-neon-cyan flex-shrink-0" />
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Requirements */}
                  <div className="border-t border-white/10 pt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-300">Requirements</p>
                    <ul className="space-y-1">
                      {device.requirements.map((req) => (
                        <li key={req} className="flex items-start gap-2 text-xs text-slate-300">
                          <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-neon-amber/60 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredDevices.length === 0 && (
            <div className="py-20 text-center">
              <Package className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <p className="text-lg text-slate-300">No devices found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose GlonexTrack Hardware */}
      <section className="gradient-section-purple border-y border-white/10 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold text-white sm:text-5xl">
              Why Choose <span className="text-gradient-warm">GlonexTrack Hardware</span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-300">
              End-to-end hardware management from procurement to deployment and ongoing support.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Wifi className="h-6 w-6" />,
                title: 'Pre-Provisioned SIM Cards',
                description: 'Every device ships with activated SIM cards, ready to connect immediately upon installation.',
                color: 'text-neon-cyan',
                bg: 'bg-neon-cyan/10',
              },
              {
                icon: <Download className="h-6 w-6" />,
                title: 'OTA Firmware Updates',
                description: 'Automatic over-the-air updates ensure your devices always run the latest firmware with security patches.',
                color: 'text-neon-purple',
                bg: 'bg-neon-purple/10',
              },
              {
                icon: <Shield className="h-6 w-6" />,
                title: 'Warranty & Replacement',
                description: 'Comprehensive warranty coverage with rapid replacement program to minimize fleet downtime.',
                color: 'text-neon-magenta',
                bg: 'bg-neon-magenta/10',
              },
              {
                icon: <Users className="h-6 w-6" />,
                title: 'Certified Technician Network',
                description: 'Nationwide network of certified installers ensures professional installation and ongoing support.',
                color: 'text-neon-teal',
                bg: 'bg-neon-teal/10',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                className="glass-card rounded-xl p-6"
              >
                <div className={`mb-4 inline-flex rounded-lg ${feature.bg} p-3 ${feature.color}`}>
                  {feature.icon}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bulk Ordering Section */}
      <section className="gradient-section py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="glass-card-heavy rounded-3xl p-8 lg:p-12">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="mb-4 inline-flex items-center gap-2 rounded-full glass-card px-4 py-2">
                  <Building2 className="h-4 w-4 text-neon-amber" />
                  <span className="text-sm font-medium text-slate-300">Enterprise Solutions</span>
                </div>
                <h2 className="mb-4 text-4xl font-bold text-white sm:text-5xl">
                  Bulk Ordering & <span className="text-gradient-cool">Custom Solutions</span>
                </h2>
                <p className="mb-6 text-lg leading-relaxed text-slate-300">
                  Scale your fleet operations with volume discounts, custom branding, and dedicated account management.
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: <ShoppingCart className="h-5 w-5" />, text: 'Volume discounts for orders 50+ units' },
                    { icon: <Tag className="h-5 w-5" />, text: 'Custom branding and packaging options' },
                    { icon: <HeadphonesIcon className="h-5 w-5" />, text: 'Dedicated account manager' },
                    { icon: <HardDrive className="h-5 w-5" />, text: 'Custom firmware configurations' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
                        {item.icon}
                      </div>
                      <span className="text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="rounded-2xl bg-gradient-to-br from-neon-cyan/10 via-neon-purple/10 to-neon-magenta/10 p-8"
              >
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-2 text-xl font-semibold text-white">Request a Quote</h3>
                    <p className="text-sm text-slate-300">
                      Get customized pricing for your fleet size and requirements.
                    </p>
                  </div>
                  <Link href="/contact">
                    <button className="w-full btn-neon rounded-lg px-6 py-3.5 text-base font-semibold flex items-center justify-center gap-2">
                      Contact Sales Team
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-slate-300">
                      <Zap className="inline h-3.5 w-3.5 mr-1 text-neon-amber" />
                      Response within 24 hours • Custom configurations available
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-hero border-t border-white/10 py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-4 text-4xl font-bold text-white sm:text-5xl">
              Ready to Equip Your Fleet?
            </h2>
            <p className="mb-8 text-lg text-slate-300">
              Let&apos;s discuss your hardware needs and create a custom solution for your fleet operations.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="/contact">
                <button className="btn-neon rounded-full px-8 py-3.5 text-base font-semibold flex items-center gap-2">
                  Get Started
                  <ArrowRight className="h-5 w-5" />
                </button>
              </Link>
              <Link href="/pricing">
                <button className="btn-neon-outline rounded-full px-8 py-3.5 text-base font-semibold">
                  View Pricing
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
